import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Wrench, PhoneCall, BookOpen, Check, X, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, revealItem } from "@/components/Reveal";
import { DuckBadge } from "@/components/DuckMascot";
import PestCard from "@/components/PestCard";
import PestJump from "@/components/PestJump";
import ArticleCard from "@/components/ArticleCard";
import CTABlock from "@/components/CTABlock";
import KidsPetsBadge from "@/components/KidsPetsBadge";
import Marquee from "@/components/Marquee";
import VideoTour, { TOUR_VIDEO } from "@/components/VideoTour";
import { PESTS } from "@/data/pests";
import { GUIDES } from "@/data/guides";

const heroImg =
  "https://images.unsplash.com/photo-1767969217509-7edd70a96e1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const headlineLines = ["Duck the bugs.", "Enjoy your", "backyard."];

const PATHS = [
  { icon: Wrench, title: "Fix it myself", desc: "DIY guides, prevention, and the tools that actually work.", to: "/diy", color: "bg-duckgreen/15 text-forest", testid: "hero-diy-path-button" },
  { icon: PhoneCall, title: "I need a pro", desc: "Help identifying the problem and finding a local provider.", to: "/hire-a-pro", color: "bg-terracotta/15 text-terracotta", testid: "hero-pro-path-button" },
  { icon: BookOpen, title: "I want to learn", desc: "Straight answers on pests, pricing, and prevention.", to: "/guides", color: "bg-teal/15 text-teal", testid: "hero-learn-path-button" },
];

// Defined once at module level: Seo re-runs its effect whenever this object's
// identity changes.
const TOUR_JSONLD = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "How DuckTheBugs works",
  description:
    "A 26-second tour of DuckTheBugs, the Connecticut homeowner's guide to bugs, bites, and backyard pests: pick what's bugging you, get straight answers, decide whether it's a DIY fix, and get pointed to a local pro.",
  thumbnailUrl: [`https://duckthebugs.com${TOUR_VIDEO.poster}`],
  uploadDate: "2026-11-03T12:00:00-05:00",
  duration: "PT26S",
  contentUrl: `https://duckthebugs.com${TOUR_VIDEO.src}`,
};

const DIY_WHEN = ["The problem is isolated", "Prevention can solve the underlying issue", "There's no significant infestation", "Simple environmental changes may fix it"];
const PRO_WHEN = ["The problem keeps returning", "There's a large infestation", "Nests or pests are in dangerous spots", "Repeated DIY attempts have failed", "Specialized treatment may be necessary"];

const KNOW_CHAPTERS = [
  { n: "01", q: "What should mosquito control cost?", to: "/guides/mosquito-control-cost" },
  { n: "02", q: "How often should treatments happen?", to: "/guides/how-often-mosquito-treat" },
  { n: "03", q: "What questions should you ask?", to: "/guides/things-to-check-before-hiring" },
  { n: "04", q: "Are contracts necessary?", to: "/guides/how-often-mosquito-treat" },
  { n: "05", q: "What should happen after treatment?", to: "/guides/things-to-check-before-hiring" },
  { n: "06", q: "When should you expect results?", to: "/guides/still-have-mosquitoes" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const featured = GUIDES.filter((g) => g.featured).slice(0, 6);

  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="DuckTheBugs is the homeowner's guide to bugs, bites, and backyard pests — straightforward DIY advice and help finding a pro when you actually need one."
        path="/"
        image={heroImg}
        jsonLd={TOUR_JSONLD}
      />

      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
          <img src={heroImg} alt="A lush green backyard at golden hour" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/55 to-forest-950/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-950/70 to-transparent" />
        </motion.div>

        <motion.div style={{ y: contentY }} className="relative w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 sm:pb-24 lg:pb-32 pt-24 sm:pt-28">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-6 sm:gap-9 lg:gap-12 items-center">
            {/* Only the text fades on scroll; the video stays fully visible. */}
            <motion.div style={{ opacity: fade }}>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2.5 mb-5 sm:mb-7 px-4 py-2 rounded-full bg-cream/10 backdrop-blur-md border border-cream/20"
              >
                <DuckBadge size={28} ring={false} />
                <span className="text-cream/90 text-xs font-mono uppercase tracking-[0.2em]">The homeowner's pest guide</span>
              </motion.div>

              <h1 className="font-serif font-black text-cream text-[13vw] sm:text-6xl lg:text-[3.6rem] xl:text-[4.4rem] leading-[0.95] tracking-tight max-w-4xl">
                {headlineLines.map((line, i) => (
                  <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
                    <motion.span
                      className="block"
                      initial={{ y: "110%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {i === 2 ? <span className="text-duckgreen italic">{line}</span> : line}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-4 sm:mt-6 text-lg sm:text-xl text-cream/85 max-w-xl leading-relaxed"
              >
                Straightforward pest-control advice, DIY solutions, and help finding a pro when you actually need one.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.85 }}
                className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3"
              >
                <a href="#whats-bugging-you" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 sm:py-4 rounded-full bg-duckgreen text-forest-950 font-bold text-base hover:bg-duckgreen/90 transition-all hover:shadow-xl hover:shadow-duckgreen/20 active:scale-95" data-testid="hero-primary-cta">
                  What's Bugging You?
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/hire-a-pro" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 sm:py-4 rounded-full bg-cream/10 backdrop-blur-md border border-cream/30 text-cream font-semibold hover:bg-cream/20 transition-all active:scale-95" data-testid="hero-secondary-cta">
                  Find a Pro
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              data-testid="home-tour"
            >
              <VideoTour />
              <p className="mt-3 text-sm text-cream/70 text-center lg:text-left">
                See how DuckTheBugs works <span className="text-cream/45">· {TOUR_VIDEO.duration}</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ---------------- THREE-PATH CHOOSER ---------------- */}
      <section className="relative -mt-14 sm:-mt-20 z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <RevealGroup className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {PATHS.map((p) => (
            <motion.div key={p.title} variants={revealItem}>
              <Link
                to={p.to}
                className="group flex flex-col h-full rounded-3xl bg-cream border border-forest/10 p-7 shadow-lg shadow-forest/5 hover:shadow-2xl hover:shadow-forest/10 hover:-translate-y-1 transition-all duration-300"
                data-testid={p.testid}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${p.color}`}>
                  <p.icon className="w-7 h-7" />
                </div>
                <h3 className="mt-5 font-serif text-2xl font-bold text-forest-950">{p.title}</h3>
                <p className="mt-2 text-forest-950/70 leading-relaxed flex-1">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-semibold text-terracotta">
                  Start here
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </RevealGroup>
      </section>

      {/* ---------------- WHAT'S BUGGING YOU ---------------- */}
      <section id="whats-bugging-you" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-28 scroll-mt-20">
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Start with the pest</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-forest-950 max-w-xl leading-[1.05]">
                What's bugging you?
              </h2>
            </div>
            <Link to="/pests" className="inline-flex items-center gap-1.5 font-semibold text-forest hover:text-terracotta transition-colors" data-testid="view-all-pests-link">
              View all pests <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal>
          <PestJump className="mb-10" />
        </Reveal>
        <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PESTS.map((pest) => (
            <PestCard key={pest.slug} pest={pest} />
          ))}
          <motion.div variants={revealItem}>
            <Link
              to="/pests"
              className="group flex flex-col justify-center items-center h-full min-h-[280px] rounded-3xl border-2 border-dashed border-forest/20 bg-sand/50 p-6 text-center hover:border-terracotta/40 hover:bg-sand transition-all"
              data-testid="pest-card-other"
            >
              <div className="w-12 h-12 rounded-2xl bg-forest/10 flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-forest" />
              </div>
              <h3 className="font-serif text-xl font-bold text-forest-950">Other Pests</h3>
              <p className="mt-1.5 text-sm text-forest-950/60">Not sure what you're dealing with? Start here.</p>
            </Link>
          </motion.div>
        </RevealGroup>
      </section>

      {/* ---------------- DIY OR CALL A PRO ---------------- */}
      <section className="bg-sand grain">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-28">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">The honest question</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-forest-950 leading-[1.05]">
                DIY or call a pro?
              </h2>
              <p className="mt-4 text-forest-950/70 text-lg">
                Sometimes you need a pro. Sometimes you just need to dump the water out of a flowerpot. We'll help you figure out which.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <Reveal>
              <div className="h-full rounded-3xl bg-card border border-duckgreen/25 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-duckgreen/15 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-forest" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-forest-950">DIY might work when…</h3>
                </div>
                <ul className="space-y-3">
                  {DIY_WHEN.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-forest-950/80">
                      <Check className="w-5 h-5 text-duckgreen mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl bg-forest-950 text-cream p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-terracotta/25 flex items-center justify-center">
                    <PhoneCall className="w-5 h-5 text-terracotta" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-cream">Call a pro when…</h3>
                </div>
                <ul className="space-y-3">
                  {PRO_WHEN.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-cream/85">
                      <X className="w-5 h-5 text-terracotta mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="mt-10 text-center">
              <Link to="/diy" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-forest-950 text-cream font-semibold hover:bg-forest transition-all active:scale-95" data-testid="help-me-decide-btn">
                Help Me Decide <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- KNOW BEFORE YOU HIRE ---------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-28">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Know before you hire</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-forest-950 leading-[1.05]">
                Know what you're paying for.
              </h2>
              <p className="mt-4 text-forest-950/70 text-lg leading-relaxed">
                Professional pest control shouldn't be a mystery. Before you sign anything, get clear on the questions that make quotes comparable — and the answers a good provider will happily give.
              </p>
              <Link to="/guides" className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta/90 transition-all active:scale-95" data-testid="read-homeowner-guides-btn">
                Read the Homeowner Guides <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <RevealGroup className="divide-y divide-forest/10 border-y border-forest/10">
            {KNOW_CHAPTERS.map((c) => (
              <motion.div key={c.n} variants={revealItem}>
                <Link to={c.to} className="group flex items-center gap-5 py-5 hover:pl-2 transition-all" data-testid={`know-chapter-${c.n}`}>
                  <span className="font-mono text-sm text-terracotta/70 w-8">{c.n}</span>
                  <span className="flex-1 font-serif text-xl sm:text-2xl font-semibold text-forest-950 group-hover:text-terracotta transition-colors">
                    {c.q}
                  </span>
                  <ArrowRight className="w-5 h-5 text-forest-950/30 group-hover:text-terracotta group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* ---------------- KIDS & PETS ---------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <Reveal>
            <div className="relative h-full min-h-[320px] rounded-[2rem] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1782248411886-60fbf8aced5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Children playing on a green lawn" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-950/40 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <KidsPetsBadge body="A huge part of pest control is really one question: how do I solve this without creating a new problem for my family? Throughout DuckTheBugs we address kids and pets directly — when it's safe to return to a treated area, what to move beforehand, what to ask a pro about the products they use, and where lower-exposure or non-chemical options fit. We never call a treatment simply 'safe' — we point you to the product label and licensed professionals for the specifics." />
          </Reveal>
        </div>
      </section>

      {/* ---------------- FIND A LOCAL PRO ---------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
        <CTABlock
          variant="dark"
          eyebrow="Find a local pro"
          title="Ready for a pro? Let's point you in the right direction."
          body="Tell us what's bugging you and where you live. We'll help connect you with an appropriate local provider — no pressure, no runaround."
          primary={{ to: "/hire-a-pro", label: "Find a Pest Control Pro" }}
          secondary={{ to: "/diy", label: "Or try DIY first" }}
        />
      </section>

      {/* ---------------- LATEST GUIDES ---------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">From the guides</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-forest-950 leading-[1.05]">
                Latest guides
              </h2>
            </div>
            <Link to="/guides" className="inline-flex items-center gap-1.5 font-semibold text-forest hover:text-terracotta transition-colors" data-testid="view-all-guides-link">
              All guides <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((g) => (
            <ArticleCard key={g.slug} guide={g} />
          ))}
        </RevealGroup>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section className="py-10 bg-sand border-y border-forest/10 overflow-hidden">
        <Marquee items={["DIY first", "Know the cost", "No scare tactics", "Kids & pets aware", "Independent"]} />
      </section>

      {/* ---------------- TRUST ---------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-28">
        <Reveal>
          <div className="relative rounded-[2.5rem] bg-forest-950 text-cream px-6 py-16 sm:px-16 sm:py-24 grain overflow-hidden text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-duckgreen/10 blur-3xl" />
            <div className="relative">
              <div className="flex justify-center mb-7">
                <DuckBadge size={80} />
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-bold text-cream leading-[1.05] max-w-3xl mx-auto">
                We're on the homeowner's side.
              </h2>
              <p className="mt-6 text-lg sm:text-xl text-cream/80 max-w-2xl mx-auto leading-relaxed">
                If you can solve the problem yourself, we'll tell you. If it's time to call a professional, we'll tell you that too. No scare tactics. No mystery. Just practical pest-control information.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/about" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-cream/10 border border-cream/30 text-cream font-semibold hover:bg-cream/20 transition-all" data-testid="trust-about-btn">
                  Our philosophy
                </Link>
                <Link to="/pests" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-duckgreen text-forest-950 font-bold hover:bg-duckgreen/90 transition-all" data-testid="trust-explore-btn">
                  Explore the pest library <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
